import { useState, useRef } from 'react';
import { useData } from '../context/DataContext';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import { Upload, FileText, X, Check, AlertCircle, Loader2, Trash2 } from 'lucide-react';
import { toast } from 'sonner';

const DataUploadDialog = ({ open, onOpenChange }) => {
  const { uploadData, isLoading } = useData();
  const [accountsFile, setAccountsFile] = useState(null);
  const [periodsFile, setPeriodsFile] = useState(null);
  const [systemName, setSystemName] = useState('');
  const [error, setError] = useState('');

  const accountsInputRef = useRef(null);
  const periodsInputRef = useRef(null);

  const handleAccountsChange = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      if (!file.name.toLowerCase().includes('accounts')) {
        toast.warning('Diese Datei scheint keine Accounts-Datei zu sein. Bitte prüfen Sie den Dateinamen.');
      }
      setAccountsFile(file);
      setError('');
    }
  };

  const handlePeriodsChange = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      if (!file.name.toLowerCase().includes('periods')) {
        toast.warning('Diese Datei scheint keine Periods-Datei zu sein. Bitte prüfen Sie den Dateinamen.');
      }
      setPeriodsFile(file);
      setError('');
    }
  };

  const handleSubmit = async () => {
    if (!accountsFile || !periodsFile) {
      setError('Bitte laden Sie beide Dateien hoch.');
      return;
    }

    if (!systemName.trim()) {
      setError('Bitte geben Sie einen Systemnamen ein.');
      return;
    }

    const result = await uploadData(accountsFile, periodsFile, systemName.trim());

    if (result.success) {
      // Check for duplicates warning
      if (result.data.warning && result.data.duplicates_csv) {
        toast.warning(result.data.warning, {
          description: 'Daten wurden geladen. Erste Einträge wurden behalten.',
          duration: 10000,
          action: {
            label: 'CSV herunterladen',
            onClick: () => {
              const blob = new Blob([result.data.duplicates_csv], { type: 'text/csv;charset=utf-8;' });
              const url = URL.createObjectURL(blob);
              const link = document.createElement('a');
              link.href = url;
              link.download = 'doppelte_eintraege.csv';
              link.click();
              URL.revokeObjectURL(url);
            }
          }
        });
      } else {
        toast.success(`Datenstand "${systemName}" erfolgreich geladen`, {
          description: `${result.data.accounts_count} Accounts, ${result.data.periods_count} Periods`
        });
      }
      handleClose();
    } else {
      setError(result.error || 'Fehler beim Hochladen der Daten');
    }
  };

  const handleClose = () => {
    setAccountsFile(null);
    setPeriodsFile(null);
    setSystemName('');
    setError('');
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-lg" data-testid="data-upload-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Upload className="h-5 w-5" style={{ color: '#2E5E99' }} />
            Datenstand laden
          </DialogTitle>
          <DialogDescription>
            Laden Sie die beiden eMatrixsys-Exportdateien hoch, um den Datenstand zu aktualisieren.
            Der bisherige Datenstand wird dabei ersetzt.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {/* System Name */}
          <div className="space-y-2">
            <Label htmlFor="systemName">Systemname</Label>
            <Input
              id="systemName"
              placeholder="z.B. vabw.eMatrixsys.io"
              value={systemName}
              onChange={(e) => setSystemName(e.target.value)}
              data-testid="system-name-input"
            />
            <p className="text-xs" style={{ color: '#5A7A9B' }}>
              Name des eMatrixsys-Systems (zur Identifikation)
            </p>
          </div>

          {/* Accounts File */}
          <div className="space-y-2">
            <Label>Accounts-Datei</Label>
            <div 
              className={`border-2 border-dashed rounded-lg p-4 text-center cursor-pointer transition-colors ${
                accountsFile ? 'border-green-500 bg-green-50' : 'border-gray-300 hover:border-[#2E5E99]'
              }`}
              onClick={() => accountsInputRef.current?.click()}
              data-testid="accounts-file-dropzone"
            >
              <input
                ref={accountsInputRef}
                type="file"
                accept=".csv"
                onChange={handleAccountsChange}
                className="hidden"
                data-testid="accounts-file-input"
              />
              {accountsFile ? (
                <div className="flex items-center justify-center gap-2 text-green-700">
                  <Check className="h-5 w-5" />
                  <span className="font-medium">{accountsFile.name}</span>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setAccountsFile(null);
                    }}
                    className="ml-2 p-1 hover:bg-green-100 rounded"
                  >
                    <X className="h-4 w-4" />
                  </button>
                </div>
              ) : (
                <div className="flex flex-col items-center gap-2" style={{ color: '#5A7A9B' }}>
                  <FileText className="h-8 w-8" />
                  <span>emie-report-accounts*.csv</span>
                  <span className="text-xs">Klicken zum Auswählen</span>
                </div>
              )}
            </div>
          </div>

          {/* Periods File */}
          <div className="space-y-2">
            <Label>Periods-Datei</Label>
            <div 
              className={`border-2 border-dashed rounded-lg p-4 text-center cursor-pointer transition-colors ${
                periodsFile ? 'border-green-500 bg-green-50' : 'border-gray-300 hover:border-[#2E5E99]'
              }`}
              onClick={() => periodsInputRef.current?.click()}
              data-testid="periods-file-dropzone"
            >
              <input
                ref={periodsInputRef}
                type="file"
                accept=".csv"
                onChange={handlePeriodsChange}
                className="hidden"
                data-testid="periods-file-input"
              />
              {periodsFile ? (
                <div className="flex items-center justify-center gap-2 text-green-700">
                  <Check className="h-5 w-5" />
                  <span className="font-medium">{periodsFile.name}</span>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setPeriodsFile(null);
                    }}
                    className="ml-2 p-1 hover:bg-green-100 rounded"
                  >
                    <X className="h-4 w-4" />
                  </button>
                </div>
              ) : (
                <div className="flex flex-col items-center gap-2" style={{ color: '#5A7A9B' }}>
                  <FileText className="h-8 w-8" />
                  <span>emie-report-periods_with_memberships*.csv</span>
                  <span className="text-xs">Klicken zum Auswählen</span>
                </div>
              )}
            </div>
          </div>

          {/* Error Message */}
          {error && (
            <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700">
              <AlertCircle className="h-5 w-5 flex-shrink-0" />
              <span className="text-sm">{error}</span>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={handleClose} disabled={isLoading}>
            Abbrechen
          </Button>
          <Button 
            onClick={handleSubmit} 
            disabled={isLoading || !accountsFile || !periodsFile || !systemName.trim()}
            data-testid="upload-submit-button"
          >
            {isLoading ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Wird hochgeladen...
              </>
            ) : (
              <>
                <Upload className="mr-2 h-4 w-4" />
                Datenstand laden
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default DataUploadDialog;
